/* The Marketplace over the real API. No X-Lumiq-Tenant header — hard rule 1.
 *
 * **This file used to be read-only, and the argument for that was half right.** It said a browser
 * upload control "would be the wrong shape", because submitting, reviewing and publishing are three
 * permissions held by different people. The separation is real and the server holds it: a submitter
 * reviewing their own artifact is refused, and publishing needs two signatures over the artifact
 * hash. What a button cannot do is *break* that — it can only be refused. Withholding the controls
 * entirely left the whole lifecycle reachable by curl alone, which is the same conclusion
 * `core.contracts`' own client reached one screen over.
 *
 * So every route the contract publishes is called from here. That sentence is a claim, and the one
 * place it was written before it was true is recorded in §9 — `features/autonomous/api` says it and
 * leaves `/evaluations` unwired. The list below is the whole of `marketplace.yaml`: capabilities,
 * plugins, my-listings, one plugin, installations, runs, run findings, upgrade plan, submit, review,
 * publication-requests, publication-decisions, publications, install, enable/disable, uninstall,
 * run, revoke, upgrade. Nineteen routes, nineteen callers.
 *
 * **Idempotency keys are per action and deliberately not uniform.** A lifecycle transition is keyed
 * on the thing it moves, so a double-click is a replay rather than a second submission. A *run* gets
 * a fresh key every time, because asking a plugin to run twice is asking twice — reusing a key would
 * replay a stale verdict and hide a real second execution from the run history, which is the durable
 * record an auditor reads.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isCapabilityCatalogue,
  isInstallation,
  isPlugin,
  isPluginRun,
  isPluginRunFinding,
  isRunResult,
  isUpgradePlan,
} from '@/shared/api/generated/marketplace';
import type {
  Capability,
  CapabilityCatalogue,
  Installation,
  Plugin,
  PluginRun,
  PluginRunFinding,
  RunResult,
  UpgradePlan,
} from '@/shared/api/generated/marketplace';
import {
  forgetIdempotencyKey,
  getJson,
  getOne,
  idempotencyKeyFor,
  patch,
  post,
  put,
  remove,
} from '@/shared/api/http';

export const PLUGINS_URL = '/v1/marketplace';

/** How many submissions the registry listing asks for.
 *
 * 200 is the route's own maximum; the default is 50, and taking it meant a deployment with more
 * than fifty submissions silently listed the first fifty as if that were all of them. Asking for
 * the maximum does not remove the bound, so a full page is reported as truncated rather than
 * presented as complete — a short list that looks whole is worse than one that says it is short. */
export const REGISTRY_LIMIT = 200;
export const INSTALLATIONS_URL = '/v1/marketplace/installations';
export const CAPABILITIES_URL = '/v1/marketplace/capabilities';
export const MY_LISTINGS_URL = '/v1/marketplace/my-listings';
export const RUNS_URL = '/v1/marketplace/runs';

const MARKETPLACE = 'marketplace';

function pluginUrl(id: string): string {
  return `${PLUGINS_URL}/${encodeURIComponent(id)}`;
}

function itemsOf(body: unknown, url: string): unknown[] {
  if (typeof body !== 'object' || body === null || !('items' in body)) {
    throw new Error(`${url} response has no items`);
  }
  const items: unknown = body.items;
  if (!Array.isArray(items)) throw new Error(`${url} items is not an array`);
  return items;
}

/* One bad item fails the whole list, and for this screen that is the only honest reading.
 *
 * A plugin dropped for failing its guard renders as a plugin nobody submitted; a run dropped renders
 * as a run nobody made. On a registry of third-party code and on its own execution log, a silently
 * shorter list is worse than a reported failure — the reader concludes less happened than did. */
function parseAll<T>(
  body: unknown,
  url: string,
  guard: (v: unknown) => v is T,
  what: string,
): T[] {
  const parsed: T[] = [];
  for (const item of itemsOf(body, url)) {
    if (!guard(item)) throw new Error(`an item did not match the ${what} schema`);
    parsed.push(item);
  }
  return parsed;
}

// ------------------------------------------------------------------ reads

export function usePlugins(): UseQueryResult<readonly Plugin[]> {
  return useQuery({
    queryKey: [MARKETPLACE, 'plugins'],
    retry: false,
    queryFn: async () =>
      parseAll(
        await getJson(`${PLUGINS_URL}?limit=${String(REGISTRY_LIMIT)}`),
        PLUGINS_URL,
        isPlugin,
        'Plugin',
      ),
  });
}

export function usePlugin(id: string | null): UseQueryResult<Plugin> {
  return useQuery({
    queryKey: [MARKETPLACE, 'plugin', id],
    enabled: id !== null,
    retry: false,
    queryFn: async () => getOne(pluginUrl(id ?? ''), isPlugin, 'plugin'),
  });
}

export function useInstallations(): UseQueryResult<readonly Installation[]> {
  return useQuery({
    queryKey: [MARKETPLACE, 'installations'],
    retry: false,
    queryFn: async () =>
      parseAll(
        await getJson(INSTALLATIONS_URL),
        INSTALLATIONS_URL,
        isInstallation,
        'Installation',
      ),
  });
}

/* Scoped to the caller by the server, and that scoping is the privacy model rather than a filter.
 * `submitted_by` is deliberately absent from every other projection, so a submitter's identity
 * cannot be read off Marketplace or a plugin detail — surfaces that are platform-wide. */
export function useMyListings(): UseQueryResult<readonly Plugin[]> {
  return useQuery({
    queryKey: [MARKETPLACE, 'my-listings'],
    retry: false,
    queryFn: async () =>
      parseAll(await getJson(MY_LISTINGS_URL), MY_LISTINGS_URL, isPlugin, 'Plugin'),
  });
}

export function useRuns(pluginId: string | null): UseQueryResult<readonly PluginRun[]> {
  const url =
    pluginId === null
      ? RUNS_URL
      : `${RUNS_URL}?plugin_id=${encodeURIComponent(pluginId)}`;
  return useQuery({
    queryKey: [MARKETPLACE, 'runs', pluginId],
    retry: false,
    queryFn: async () => parseAll(await getJson(url), url, isPluginRun, 'PluginRun'),
  });
}

export function useRunFindings(
  runId: string | null,
): UseQueryResult<readonly PluginRunFinding[]> {
  const url = `${RUNS_URL}/${encodeURIComponent(runId ?? '')}/findings`;
  return useQuery({
    queryKey: [MARKETPLACE, 'run-findings', runId],
    enabled: runId !== null,
    retry: false,
    queryFn: async () =>
      parseAll(await getJson(url), url, isPluginRunFinding, 'PluginRunFinding'),
  });
}

export function useCapabilityCatalogue(): UseQueryResult<CapabilityCatalogue> {
  return useQuery({
    queryKey: [MARKETPLACE, 'capabilities'],
    retry: false,
    queryFn: async () => {
      const body = await getJson(CAPABILITIES_URL);
      if (!isCapabilityCatalogue(body)) {
        throw new Error('the capability catalogue did not match its schema');
      }
      return body;
    },
  });
}

/* The plan is a *read*, authorized as `read` rather than `install`, so somebody who cannot upgrade
 * can still see what an upgrade would do. Asking what a change costs must not require permission to
 * make it. */
export function useUpgradePlan(
  pluginId: string | null,
  toVersion: string | null,
): UseQueryResult<UpgradePlan> {
  const url = `${pluginUrl(pluginId ?? '')}/upgrade-plan?to_version=${encodeURIComponent(
    toVersion ?? '',
  )}`;
  return useQuery({
    queryKey: [MARKETPLACE, 'upgrade-plan', pluginId, toVersion],
    enabled: pluginId !== null && toVersion !== null,
    retry: false,
    queryFn: async () => getOne(url, isUpgradePlan, 'upgrade plan'),
  });
}

// ------------------------------------------------------------------ writes

function useInvalidate(): () => void {
  const client = useQueryClient();
  return () => void client.invalidateQueries({ queryKey: [MARKETPLACE] });
}

export interface SubmitRequest {
  readonly name: string;
  readonly version: string;
  readonly artifact: string;
  readonly capabilities: readonly Capability[];
}

/* Keyed on name and version, which is the submission's natural identity — the server refuses a
 * duplicate pair with a 409, so a replayed key and a duplicate submission answer the same way. */
export function useSubmitPlugin(): UseMutationResult<Plugin, Error, SubmitRequest> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (request: SubmitRequest) =>
      post(
        PLUGINS_URL,
        idempotencyKeyFor('plugin-submit', `${request.name}@${request.version}`),
        isPlugin,
        'plugin',
        {
          // **These names are the contract's, and getting them wrong is silent.** The route reads
          // `body.get("artifact_base64")` and `body.get("declared_capabilities", [])`, so a body
          // spelled `artifact` and `capabilities` submitted an empty artifact and no declared
          // capability — refused as "a plugin submission carries an artifact", which reads as a
          // problem with the file somebody picked. FastAPI takes the whole body as a dict here, so
          // an unknown key is dropped rather than rejected; nothing on either side could catch it.
          name: request.name,
          version: request.version,
          artifact_base64: request.artifact,
          declared_capabilities: [...request.capabilities],
        },
      ),
    onSuccess: (_plugin, request) => {
      forgetIdempotencyKey('plugin-submit', `${request.name}@${request.version}`);
      invalidate();
    },
  });
}

export function useReviewPlugin(): UseMutationResult<Plugin, Error, string> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (pluginId: string) =>
      post(
        `${pluginUrl(pluginId)}/reviews`,
        idempotencyKeyFor('plugin-review', pluginId),
        isPlugin,
        'plugin',
        {},
      ),
    onSuccess: (_plugin, pluginId) => {
      forgetIdempotencyKey('plugin-review', pluginId);
      invalidate();
    },
  });
}

export function useRequestPublication(): UseMutationResult<unknown, Error, string> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (pluginId: string) => {
      const body = await post(
        `${pluginUrl(pluginId)}/publication-requests`,
        idempotencyKeyFor('plugin-pub-request', pluginId),
        (value): value is unknown => value !== null,
        'publication request',
        {},
      );
      return body;
    },
    onSuccess: (_body, pluginId) => {
      forgetIdempotencyKey('plugin-pub-request', pluginId);
      invalidate();
    },
  });
}

export interface PublicationDecision {
  readonly pluginId: string;
  readonly approve: boolean;
  readonly reason: string;
}

/* Keyed on the plugin *and* the direction. A signature and a rejection are different decisions, and
 * one key across both would make a rejection after an approval replay the approval. */
export function useDecidePublication(): UseMutationResult<
  Plugin,
  Error,
  PublicationDecision
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (decision: PublicationDecision) =>
      post(
        `${pluginUrl(decision.pluginId)}/publication-decisions`,
        idempotencyKeyFor(
          'plugin-pub-decision',
          `${decision.pluginId}:${decision.approve ? 'approve' : 'reject'}`,
        ),
        isPlugin,
        'plugin',
        { approve: decision.approve, reason: decision.reason },
      ),
    onSuccess: (_plugin, decision) => {
      forgetIdempotencyKey(
        'plugin-pub-decision',
        `${decision.pluginId}:${decision.approve ? 'approve' : 'reject'}`,
      );
      invalidate();
    },
  });
}

export function usePublishPlugin(): UseMutationResult<Plugin, Error, string> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (pluginId: string) =>
      post(
        `${pluginUrl(pluginId)}/publications`,
        idempotencyKeyFor('plugin-publish', pluginId),
        isPlugin,
        'plugin',
        {},
      ),
    onSuccess: (_plugin, pluginId) => {
      forgetIdempotencyKey('plugin-publish', pluginId);
      invalidate();
    },
  });
}

export interface RevokeRequest {
  readonly pluginId: string;
  readonly reason: string;
}

export function useRevokePlugin(): UseMutationResult<Plugin, Error, RevokeRequest> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (request: RevokeRequest) =>
      post(
        `${pluginUrl(request.pluginId)}/revocations`,
        idempotencyKeyFor('plugin-revoke', request.pluginId),
        isPlugin,
        'plugin',
        { reason: request.reason },
      ),
    onSuccess: (_plugin, request) => {
      forgetIdempotencyKey('plugin-revoke', request.pluginId);
      invalidate();
    },
  });
}

export interface InstallRequest {
  readonly pluginId: string;
  readonly capabilities: readonly Capability[];
}

/* A PUT with no key, and the absence is the point: the installation's natural key is the URL, so the
 * route is idempotent by construction and a key would be ceremony over a request that cannot
 * duplicate anything. */
export function useInstallPlugin(): UseMutationResult<
  Installation,
  Error,
  InstallRequest
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (request: InstallRequest) =>
      put(`${pluginUrl(request.pluginId)}/installation`, isInstallation, 'installation', {
        // `granted_capabilities`, not `capabilities`. The route reads the former and defaults it to
        // `[]`, so every install refused with "an installation grants at least one capability" no
        // matter how many boxes were ticked — an error about the selection, produced by a body that
        // never carried it.
        granted_capabilities: [...request.capabilities],
      }),
    onSuccess: invalidate,
  });
}

export interface EnabledRequest {
  readonly pluginId: string;
  readonly enabled: boolean;
}

export function useSetInstallationEnabled(): UseMutationResult<
  Installation,
  Error,
  EnabledRequest
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (request: EnabledRequest) =>
      patch(
        `${pluginUrl(request.pluginId)}/installation/state`,
        isInstallation,
        'installation',
        { enabled: request.enabled },
      ),
    onSuccess: invalidate,
  });
}

export function useUninstallPlugin(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (pluginId: string) => remove(`${pluginUrl(pluginId)}/installation/state`),
    onSuccess: invalidate,
  });
}

export interface RunRequest {
  readonly pluginId: string;
  readonly datasetId: string;
}

/* A fresh key every time, deliberately — see this file's header. Two runs are two runs. */
export function useRunPlugin(): UseMutationResult<RunResult, Error, RunRequest> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (request: RunRequest) =>
      post(
        `${pluginUrl(request.pluginId)}/runs`,
        crypto.randomUUID(),
        isRunResult,
        'run result',
        { dataset_id: request.datasetId },
      ),
    onSuccess: invalidate,
  });
}

export interface UpgradeRequest {
  readonly pluginId: string;
  readonly toVersion: string;
}

export function useUpgradePlugin(): UseMutationResult<
  Installation,
  Error,
  UpgradeRequest
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (request: UpgradeRequest) =>
      post(
        `${pluginUrl(request.pluginId)}/upgrades`,
        idempotencyKeyFor('plugin-upgrade', `${request.pluginId}->${request.toVersion}`),
        isInstallation,
        'installation',
        { to_version: request.toVersion },
      ),
    onSuccess: (_installation, request) => {
      forgetIdempotencyKey('plugin-upgrade', `${request.pluginId}->${request.toVersion}`);
      invalidate();
    },
  });
}
